import React, { useEffect, useRef } from 'react'
import { Formik, FormikErrors } from 'formik'
import { useIntl } from 'react-intl'
import {
  MagentoCustomizableCheckboxValue,
  MagentoCustomizableDropDownValue,
  MagentoCustomizableOptionInterface,
  MagentoCustomizableRadioValue
} from 'lib/types'
import { SimpleProductItemCustomOption } from 'lib/core/data/quote/addProduct/types'
import { Form } from 'lib/ui'
import { Option } from './components/Option'
import { validation, getOptionId, getOptionName, getStringParsedValue } from './schema'

type Values = Record<string, string[] | string>

export type CustomOptionsValues = Record<string, {optionId: number, value: string }>

export interface CustomOptionsEvent {
  errors: FormikErrors<Values>
  values: CustomOptionsValues
  priceDiff: number
}

interface OptionProps {
  label: string
  name: string
  isRequired: boolean
}

export interface CustomOptionsOption<T> {
  key: string
  data: T
  value: string
  formattedPrice: string
}

export interface CheckboxRendererProps extends OptionProps {
  options: Array<CustomOptionsOption<MagentoCustomizableCheckboxValue>>
}

export interface RadioRendererProps extends OptionProps {
  options: Array<CustomOptionsOption<MagentoCustomizableRadioValue>>
}

export interface DropDownRendererProps extends OptionProps {
  options: Array<CustomOptionsOption<MagentoCustomizableDropDownValue>>
}

export interface Renderer {
  Checkbox?: (props: CheckboxRendererProps) => JSX.Element
  Radio?: (props: RadioRendererProps) => JSX.Element
  DropDown?: (props: DropDownRendererProps) => JSX.Element
}

interface Props {
  options?: MagentoCustomizableOptionInterface[]
  onChange?: (event: CustomOptionsEvent) => void
  renderer?: Renderer
}

export const CustomOptions = ({ options, onChange, renderer }: Props) => {
  const intl = useIntl()
  const hasRequiredOptions = Boolean(options?.find(opt => opt.required))
  const onChangeCallback = useRef(onChange).current

  useEffect(() => {
    if (hasRequiredOptions && onChangeCallback) {
      onChangeCallback({
        values: {},
        errors: { requiredOptions: '' },
        priceDiff: 0
      })
    }
  }, [hasRequiredOptions, onChangeCallback])

  if (!options?.length) {
    return null
  }

  const handleFormChange = (values: Values, errors: FormikErrors<Values>) => {
    let priceDiff = 0
    const customOptionsValues = Object.keys(values).reduce((obj: CustomOptionsValues, name) => {
      const optionId = getOptionId(name)
      const optionValue = values[name]
      obj[optionId] = {
        optionId,
        value: getStringParsedValue(optionValue)
      }

      return obj
    }, {})

    options.forEach(option => {
      const {
        field_value: fieldValue,
        area_value: areaValue,
        dropdown_value: dropdownValue,
        checkbox_value: checkboxValue,
        radio_value: radioValue
      } = option

      const value = values[getOptionName(option)]

      if (fieldValue && value) {
        priceDiff += fieldValue.price
      }

      if (areaValue && value) {
        priceDiff += areaValue.price
      }

      if (dropdownValue && value && typeof value === 'string') {
        const optValue = dropdownValue.find(el => el.option_type_id === parseInt(value))
        priceDiff += optValue?.price ?? 0
      }

      if (checkboxValue && value && Array.isArray(value)) {
        value.forEach(value => {
          const optValue = checkboxValue.find(el => el.option_type_id.toString() === value)
          priceDiff += optValue?.price ?? 0
        })
      }

      if (radioValue && value && Array.isArray(value)) {
        value.forEach(value => {
          const optValue = radioValue.find(el => el.option_type_id.toString() === value)
          priceDiff += optValue?.price ?? 0
        })
      }
    })

    onChange?.({
      errors,
      values: customOptionsValues,
      priceDiff
    })
  }

  return (
    <div>
      <Formik
        initialValues={{}}
        onSubmit={() => undefined}
        validationSchema={validation({ options, intl })}
        validateOnChange
      >
        <Form.Wrapper>
          <Form.FormikObserver<Values>
            autoSubmit
            onChange={handleFormChange}
          />
          {options?.map(option => {
            return (
              <Option
                key={option.option_id}
                option={option}
                renderer={renderer}
              />
            )
          })}
        </Form.Wrapper>
      </Formik>
    </div>
  )
}

export const getCustomizableOptions = (customOptions?: CustomOptionsEvent): SimpleProductItemCustomOption[] | undefined => {
  return customOptions ? Object.values(customOptions.values).map(el => ({ id: el.optionId, value_string: el.value })) : undefined
}
